import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

export class CsRecentlyViewed extends Component {
  async connectedCallback() {
    super.connectedCallback();

    const currentProductId = this.dataset.productId;
    const max = parseInt(this.dataset.maxProducts, 10) || 4;
    const layout = this.dataset.layoutType || 'grid';

    const ids = RecentlyViewed.getProducts()
      .filter((p) => String(p.id) !== String(currentProductId))
      .slice(0, max);

    if (!ids.length) {
      this.setAttribute('data-error', 'empty');
      return;
    }

    try {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', ids.map((p) => 'id:' + p.id).join(' OR '));
      url.searchParams.set('resources[type]', 'product');

      const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const cards = Array.from(doc.querySelectorAll('.resource-list__item'));

      if (!cards.length) {
        this.setAttribute('data-error', 'empty');
        return;
      }

      const skeletons = this.refs.skeletonItems || [];

      // Section already outputs .resource-list__item wrappers; use them directly
      const items = cards;

      const grid = this.querySelector('.resource-list');
      if (!grid) return;

      if (layout === 'carousel') {
        this.#buildCarousel(grid, items);
        if (this.refs.mobileCarousel) {
          this.#buildCarousel(this.refs.mobileCarousel, items);
        }
      } else {
        // Replace skeletons in-place to prevent height collapse
        items.forEach((item, i) => {
          if (skeletons[i]) {
            skeletons[i].replaceWith(item);
          } else {
            grid.appendChild(item);
          }
        });
        // Remove any remaining skeletons (if cards < skeletons)
        skeletons.slice(items.length).forEach((s) => s.remove());
        if (this.refs.mobileCarousel) {
          this.#buildCarousel(this.refs.mobileCarousel, items);
        }
      }
    } catch {
      this.setAttribute('data-error', 'empty');
    }
  }

  #buildCarousel(container, cards) {
    // Skeletons render in a page-width .resource-list--grid to stay size-constrained
    // during load. Swap to the full-bleed carousel container here.
    container.classList.remove('resource-list--grid');
    container.classList.add('force-full-width');

    const sectionWidth = this.dataset.sectionWidth || 'page-width';
    const iconsStyle = this.dataset.iconsStyle || 'arrow';
    const iconsShape = this.dataset.iconsShape || 'none';
    const shapeClass = iconsShape !== 'none' ? ` slideshow-control--shape-${iconsShape}` : '';
    const columnsGap = parseInt(this.dataset.columnsGap, 10) || 16;
    const gutter = sectionWidth === 'page-width' ? 'start end' : '';

    const timelineScope = cards.map((_, i) => `--slide-${i}`).join(', ');
    const showArrows = iconsStyle !== 'none' && cards.length > 1;
    const icon = iconsStyle.includes('chevron')
      ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 18l6-6-6-6"/></svg>'
      : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>';

    const slideCards = cards.map((card, i) => `
      <slideshow-slide
        ref="slides[]"
        aria-hidden="${i === 0 ? 'false' : 'true'}"
        style="--slideshow-timeline: --slide-${i}"
        class="resource-list__slide"
      >
        ${card.outerHTML}
      </slideshow-slide>
    `).join('');

    container.innerHTML = `
      <div style="--gutter-slide-width: ${gutter ? 'var(--util-page-margin-offset)' : '0px'}; --resource-list-column-gap: ${columnsGap}px;">
        <slideshow-component
          class="resource-list__carousel"
          style="--slideshow-timeline: ${timelineScope}"
          initial-slide="0"
        >
          <slideshow-container ref="slideshowContainer">
            ${showArrows ? `
              <slideshow-arrows position="center">
                <button class="slideshow-control slideshow-control--previous slideshow-control--style-${iconsStyle}${shapeClass} button button-unstyled button-unstyled--transparent flip-x" on:click="/previous" ref="previous">
                  <span class="svg-wrapper icon-arrow">${icon}</span>
                </button>
                <button class="slideshow-control slideshow-control--next slideshow-control--style-${iconsStyle}${shapeClass} button button-unstyled button-unstyled--transparent" on:click="/next" ref="next">
                  <span class="svg-wrapper icon-arrow">${icon}</span>
                </button>
              </slideshow-arrows>
            ` : ''}
            <slideshow-slides tabindex="-1" ref="scroller" ${gutter ? `gutters="${gutter}"` : ''}>
              ${slideCards}
            </slideshow-slides>
          </slideshow-container>
        </slideshow-component>
      </div>
    `;
  }
}

if (!customElements.get('cs-recently-viewed')) {
  customElements.define('cs-recently-viewed', CsRecentlyViewed);
}
